import React, { useCallback, useState } from "react";
import * as styles from "./OfferUrgencyBox.module.scss";
import { Button, Icon } from "@silversea-ssc/ui-components-react";
import { useElementHeightObserver } from "./utils";
import { forceResTimezone } from "src/utils/date/forceResTimezone";
import { useLingui } from "@lingui/react/macro";
const Countdown = React.lazy(() => import("components/Countdown/Countdown"));

type Props = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  title: string;
  content: string;
  countdownLabel?: string;
  expirationDate?: Date;
  ctaUrl?: string;
  ctaLabel?: string;
  showCountdown: boolean;
};

const OfferUrgencyBox: React.FC<Props> = ({
  title,
  content,
  countdownLabel,
  expirationDate,
  ctaUrl,
  ctaLabel,
  showCountdown,
  ...otherProps
}) => {
  const { t } = useLingui();
  const [isVisible, setIsVisible] = useState(true);
  const onClose = useCallback(() => setIsVisible(false), []);

  const positionBottom = useElementHeightObserver(
    `div[data-content-name="keep_booking_box"],div[data-content-name="courtesy_hold_box"]`
  );

  const cssVariables = {
    "--position-bottom": `${positionBottom}px`
  } as React.CSSProperties;

  if (!isVisible) return null;

  const isCountdownVisible = showCountdown && expirationDate && expirationDate > new Date();

  return (
    <div
      className={styles.wrapper}
      data-content-name="urgency-box"
      style={cssVariables}
      {...otherProps}
    >
      <div className={styles.box}>
        <Icon
          name="discount-chip"
          padding={false}
          wrapperClassName={styles.iconWrapper}
          className={styles.icon}
        />
        <Button
          renderAs="button"
          iconName="close"
          variant="inline-text"
          size="small"
          className={styles.closeButton}
          dataContentPiece="close"
          onClick={onClose}
          aria-label="Close"
        />
        <div className={styles.title}>{title}</div>
        <div className={styles.content}>
          {content}
          {isCountdownVisible && (
            <div className={styles.countdown}>
              <React.Suspense fallback={null}>
                <Countdown
                  expireDate={forceResTimezone(expirationDate)}
                  label={countdownLabel ?? t`OFFER ENDS IN`}
                  variant="small"
                />
              </React.Suspense>
            </div>
          )}
        </div>
        {ctaUrl && (
          <div className={styles.footer}>
            <Button
              renderAs="a"
              href={ctaUrl}
              variant="inline-text"
              size="small"
              dataContentPiece="book_your_cruise"
              className={styles.cta}
            >
              {ctaLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferUrgencyBox;
